import _isEmpty from "lodash/isEmpty";

const onError = (error) => {
    const { response: { data = null } } = error;
    let message = "An error has occurred fetching the series";
    if (data) {
        message = data.message;
    }
    toastr.error(message, "Error");
};

const state = {
    currentPrebuild: {
        prebuildId: "",
        variantId: ""
    },
    series_info: {},
    prebuilds: [],
    reviews: {},
    cart: {},
    accessories: {},
    rules: [],
    isCartUpdating: false
};

const getters = {
    getSeriesInfo(state) {
        return state.series_info || {};
    },
    getSeriesPrebuilds(state) {
        return state.prebuilds || [];
    },
    getPrebuildsReviews(state) {
        return state.reviews || {};
    },
    getCurrentPrebuildId(state) {
        return state.currentPrebuild.prebuildId;
    },
    getCurrentVariantId(state) {
        return state.currentPrebuild.variantId;
    },
    getCartInfo(state) {
        return state.cart || {};
    },
    getSeriesAccessories(state) {
        return state.accessories || {};
    },
    getCurrentAccessories(state) {
        return state.cart.accessories || [];
    },
    getIsCartUpdating(state) {
        return state.isCartUpdating;
    }
};

const mutations = {
    SET_SERIES_INFO(state, payload) {
        state.series_info = payload;
    },
    SET_SERIES_PREBUILDS(state, payload) {
        state.prebuilds = payload;
    },
    SET_PREBUILDS_REVIEWS(state, payload) {
        state.reviews = payload;
    },
    SET_PREBUILD_REVIEWS(state, { slug, reviews }) {
        const mergeReviews = {
            ...reviews,
            data: [...state.reviews[slug].data, ...reviews.data]
        };
        state.reviews[slug] = mergeReviews;
    },
    SET_CURRENT_PREBUILD_ID(state, payload) {
        state.currentPrebuild.prebuildId = payload;
    },
    SET_CURRENT_VARIANT_ID(state, payload) {
        state.currentPrebuild.variantId = payload;
    },
    SET_SERIES_CART(state, payload) {
        state.cart = payload;
    },
    SET_SERIES_CART_ACCESSORIES(state, payload) {
        state.accessories = payload
    },
    SET_IS_CART_UPDATING(state, payload) {
        state.isCartUpdating = payload;
    },
    SET_RULES(state, payload) {
        state.rules = payload;
    }
};

/**
 * @todo dynamic cart routes
 */

const actions = {
    setPrebuildSeries({ commit }, payload) {
        const { seriesInfo, prebuilds, reviews } = payload;
        commit("SET_SERIES_INFO", seriesInfo);
        commit("SET_SERIES_PREBUILDS", prebuilds);
        commit("SET_PREBUILDS_REVIEWS", reviews);
    },
    setPrebuildId({ commit }, payload = "") {
        commit("SET_CURRENT_PREBUILD_ID", payload);
    },
    setVariantId({ commit }, payload = "") {
        commit("SET_CURRENT_VARIANT_ID", payload);
    },
    setSeriesCart({ commit }, payload) {
        commit("SET_SERIES_CART", payload);
    },
    setSeriesAccessories({ commit }, payload) {
        commit("SET_SERIES_CART_ACCESSORIES", payload);
    },
    setIsCartUpdating({ commit }, payload) {
        commit("SET_IS_CART_UPDATING", payload);
    },
    setRules({ commit }, payload) {
        commit("SET_RULES", payload)
    },
    async fetchSeriesInfo(context, payload) {
        const { baseEndpoint = "/prebuild-series", name } = payload;

        try {
            const { data: seriesInfo } = await axios({
                method: "get",
                url: `${baseEndpoint}/${name}`,
                headers: {
                    "token": window.CONFIG.SESSION
                }
            });
            return seriesInfo;
        } catch(error) {
            onError(error);
        }
    },
    async fetchSeriesPrebuilds({ dispatch }, payload) {
        let prebuilds = [];
        const { seriesInfo: { prebuilds: series_prebuilds } } = payload;

        for (const series_prebuild of series_prebuilds) {
            const { data } = await dispatch("fetchSeriesPrebuild", series_prebuild);
            prebuilds = [...prebuilds, data];
        }

        return prebuilds;
    },
    async fetchSeriesPrebuild(context, payload) {
        const { slug } = payload;

        try {
            return await axios({
                method: "get",
                url: `/prebuilds/${slug}`,
                headers: {
                    "token": window.CONFIG.SESSION
                }
            });
        } catch(error) {
            onError(error);
        }
    },
    async fetchPrebuildsReviews({ dispatch }, payload) {
        const { prebuilds, page, limit, sort } = payload;
        let reviews = {};

        for (const prebuild of prebuilds) {
            const { slug } = prebuild;
            const { data } = await dispatch("fetchReviewsBySlug", { slug, page, limit, sort });
            reviews = {
                ...reviews,
                [slug]: data
            };
        }

        return reviews;
    },
    async fetchReviewsBySlug(context, { slug, page, limit, sort }) {
        try {
            return await axios({
                method: "get",
                url: `/prebuilds/${slug}/reviews`,
                headers: {
                    "token": window.CONFIG.SESSION
                },
                params: {
                    page,
                    limit,
                    sort
                }
            });
        } catch (error) {
            onError(error);
        }
    },
    async setReviewsByPagination({ dispatch, commit }, payload) {
        const { slug } = payload;
        const { data: reviews } = await dispatch("fetchReviewsBySlug", payload);
        commit("SET_PREBUILD_REVIEWS", { slug, reviews });
    },
    async initSeriesCart({ dispatch }, variantId) {
        const variantData = new FormData();
        variantData.append("build_variant_id", variantId);

        try {
            await axios({
                method: "POST",
                url: `/prebuild-series/starter-line/cart/init`,
                headers: {
                    token: window.CONFIG.SESSION
                },
                data: variantData
            });

            localStorage.setItem("series-cart-Initialized", true);


            await dispatch("getSeriesCart");
            await dispatch("getSeriesAccessories");
        } catch (error) {
            onError(error);
        }
    },
    async getSeriesCart({ dispatch }) {
        try {
            const { data } = await axios({
                method: "get",
                url: `/prebuild-series/starter-line/cart`,
                headers: {
                    "token": window.CONFIG.SESSION
                }
            });

            dispatch("getSeriesAccessories");
            dispatch("setSeriesCart", data);
        } catch (error) {
            onError(error);
        }
    },
    async getSeriesAccessories({ dispatch }) {
        try {
            const { data: accessories } = await axios({
                method: "get",
                url: `/prebuild-series/starter-line/accessories`,
                headers: {
                    "token": window.CONFIG.SESSION
                }
            });

            dispatch("setSeriesAccessories", accessories);
        } catch (error) {
            onError(error);
        }
    },
    async updateAccessories({ dispatch }, payload) {
        dispatch("setIsCartUpdating", true);

        const { product_id, variant_id, quantity } = payload;

        const productData = new FormData();
        productData.append("product_id", product_id);
        productData.append("variant_id", variant_id);
        productData.append("quantity", quantity);

        try {
            const { data } = await axios({
                method: "POST",
                url: `/prebuild-series/starter-line/cart/accessories`,
                headers: {
                    token: window.CONFIG.SESSION
                },
                data: productData
            });

            if (!_isEmpty(data.rules)) {
                dispatch("setRules", data.rules);
            }

            dispatch("setSeriesCart", data);
            dispatch("setIsCartUpdating", false);
        } catch (error) {
            onError(error);
        }
    },
    async removeAccessory({ dispatch }, accesoryId) {
        dispatch("setIsCartUpdating", true);

        try {
            const { data } = await axios({
                method: "DELETE",
                url: `/prebuild-series/starter-line/cart/accessories/${ accesoryId }`,
                headers: {
                    token: window.CONFIG.SESSION
                }
            });

            dispatch("setSeriesCart", data);
            dispatch("setIsCartUpdating", false);
        } catch (error) {
            onError(error);
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
